/**
 * DeliveryPanel — the last screen before a book leaves.
 * ===========================================================================
 *
 * ⛔ THE GAP THIS FILLS. Every other panel answers a question about work in progress. Nothing answered
 * the question asked once, at the end: is this book FINISHED, and is the thing I am about to send
 * actually the book? The finished export was the only artifact nobody checked, and its share link lived
 * in React state — a 1,217 MB zip sat intact in storage and unreachable from the app after one reload.
 *
 * Four things, in the order an operator needs them:
 *   1. one verdict, and what blocks it
 *   2. the ACX values actually ACHIEVED — the worst chapter, not the average
 *   3. every artifact ever produced, with a link that can always be re-minted
 *   4. the two tools: verify the zip, and audition every chapter join
 *
 * ⭐ THE WORST CHAPTER IS THE ONLY NUMBER THAT MATTERS, which is why this panel leads with it. A
 * distributor rejects a BOOK for one bad file. An average peak of −6 dB is worthless if chapter 41 sits
 * at −3.05 against a −3.0 ceiling, and an average is exactly what hides that.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Button, Header, Icon, Label, Message, Popup, Segment, Table, Loader,
} from "semantic-ui-react";

import {
  fetchDeliveryReport, fetchArtifactLink, verifyArtifact, buildSeamReel,
} from "../../../slices/voiceRenderingSlice";
import CollapsibleSection from "../../../components/voiceRendering/CollapsibleSection";

const VERDICT = {
  ready: { color: "green", icon: "check circle", text: "Ready to deliver" },
  unproven: { color: "yellow", icon: "question circle", text: "Unproven" },
  "not-ready": { color: "red", icon: "warning sign", text: "Not ready" },
};

const fmtBytes = (b) => (b == null ? "—" : `${(b / 1048576).toFixed(1)} MB`);
const fmtHours = (ms) => {
  if (!ms) return "—";
  const h = Math.floor(ms / 3600000);
  const m = Math.round((ms % 3600000) / 60000);
  return h ? `${h} h ${m} min` : `${m} min`;
};
const fmtClock = (ms) => {
  if (ms == null) return "—";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

/**
 * One ACX check, as achieved across the whole book.
 *
 * ⚠️ THE MARGIN IS THE HEADLINE, not the value. "−3.05 dB" means nothing without "0.05 dB from the
 * limit", and the second is what tells the operator whether to go back to the mastering preset.
 */
const CheckRow = ({ check }) => {
  const tight = check.tightestMargin;
  const failed = check.failed.length > 0;
  /**
   * ⚠️ "THIN MARGIN" ONLY MEANS SOMETHING FOR A MEASURED LEVEL, and the first version of this row got
   * that wrong. Sample rate is an `equals` check and bitrate a `min` one; both are satisfied EXACTLY by
   * the encoder settings, so both report a margin of 0.00 — and a naive "under 1 unit of room" rule
   * painted the two most certain rows in the table as warnings, next to a peak check that really was
   * close. Gated on dB, which is exactly the set of checks that drift chapter to chapter (RMS, peak,
   * noise floor) and excludes the structural ones that are either right or wrong.
   */
  const continuous = check.unit === "dB" && check.equals == null;
  const thin = continuous && tight != null && tight >= 0 && tight < 1;
  const exact = check.equals != null;
  const limit = check.max != null ? `≤ ${check.max}` : check.min != null ? `≥ ${check.min}`
    : check.equals != null ? `= ${check.equals}` : "—";

  return (
    <Table.Row negative={failed} warning={!failed && thin}>
      <Table.Cell>{check.label}</Table.Cell>
      <Table.Cell textAlign="right">{limit} {check.unit}</Table.Cell>
      <Table.Cell textAlign="right">
        {check.tightestValue == null ? "—" : `${check.tightestValue} ${check.unit}`}
      </Table.Cell>
      <Table.Cell textAlign="right">
        {tight == null ? "—" : (
          <strong style={{ color: failed ? "#912d2b" : thin ? "#794b02" : undefined }}>
            {/* An exact-match check has no "room left" to report — saying "0.00 left" reads as danger. */}
            {tight < 0 ? `over by ${Math.abs(tight).toFixed(2)}`
              : exact ? "exact"
                : `${tight.toFixed(2)} ${check.unit} left`}
          </strong>
        )}
      </Table.Cell>
      <Table.Cell>
        {check.tightestChapter == null ? "—" : `ch ${check.tightestChapter}`}
      </Table.Cell>
      <Table.Cell>
        {failed ? (
          <Label color="red" size="small">{check.failed.length} fail</Label>
        ) : check.unmeasured.length ? (
          <Label size="small">{check.unmeasured.length} unmeasured</Label>
        ) : (
          <Label basic color="green" size="small">{check.passed} pass</Label>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

const DeliveryPanel = ({ bookId, selector = "book.full", unitLabel }) => {
  const dispatch = useDispatch();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [link, setLink] = useState(null);           // { uuid, signedUrl, expiresAt }
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(null); // uuid in flight
  const [verified, setVerified] = useState({});     // uuid -> result
  const [reeling, setReeling] = useState(false);
  const [openReel, setOpenReel] = useState(null);   // uuid whose join list is expanded
  const [reelMsg, setReelMsg] = useState(null);

  const load = useCallback(async () => {
    if (!bookId || !selector) return;
    setLoading(true);
    setError(null);
    try {
      setReport(await dispatch(fetchDeliveryReport({ bookId, selector })).unwrap());
    } catch (e) {
      setError(e?.message || "Could not read the delivery report");
    } finally {
      setLoading(false);
    }
  }, [dispatch, bookId, selector]);

  useEffect(() => { load(); }, [load]);

  // A scope change invalidates everything on screen — these numbers belong to the old unit.
  useEffect(() => {
    setLink(null);
    setVerified({});
    setReelMsg(null);
    setOpenReel(null);
  }, [selector, bookId]);

  const getLink = useCallback(async (uuid) => {
    setCopied(false);
    try {
      const r = await dispatch(fetchArtifactLink({ uuid })).unwrap();
      setLink({ uuid, ...r });
    } catch (e) {
      setError(e?.message || "Could not mint a link for that file");
    }
  }, [dispatch]);

  const runVerify = useCallback(async (uuid) => {
    setVerifying(uuid);
    try {
      const r = await dispatch(verifyArtifact({ uuid })).unwrap();
      setVerified((prev) => ({ ...prev, [uuid]: r }));
    } catch (e) {
      setVerified((prev) => ({ ...prev, [uuid]: { error: e?.message || "Verify failed" } }));
    } finally {
      setVerifying(null);
    }
  }, [dispatch]);

  const runReel = useCallback(async () => {
    setReeling(true);
    setReelMsg(null);
    try {
      const r = await dispatch(buildSeamReel({ bookId, selector })).unwrap();
      setReelMsg(`Built ${r.boundaries} join${r.boundaries === 1 ? "" : "s"} — ${fmtBytes(r.bytes)}, `
        + `${fmtHours(r.durationMs)} to listen.`);
      await load();
    } catch (e) {
      setError(e?.message || "Could not build the seam reel");
    } finally {
      setReeling(false);
    }
  }, [dispatch, bookId, selector, load]);

  const zips = useMemo(
    () => (report?.artifacts || []).filter((a) => a.kind === "export-zip"),
    [report]
  );
  const reels = useMemo(
    () => (report?.artifacts || []).filter((a) => a.kind === "seam-reel"),
    [report]
  );

  /**
   * ⭐ WHAT EACH FOLDED SECTION SAYS FROM THE OUTSIDE.
   *
   * These exist so that collapsing costs nothing. A header reading "ACX, as achieved ▸" would make the
   * operator open it to learn anything at all, and then every section is open again and the scroll is
   * back — so the number that would have made them look goes on the outside instead.
   */
  const acxSummary = useMemo(() => {
    if (!report?.acx?.checks?.length) return "nothing measured yet";
    const dB = report.acx.checks.filter((c) => c.unit === "dB" && c.tightestMargin != null);
    const tightest = dB.sort((a, b) => a.tightestMargin - b.tightestMargin)[0];
    const parts = [`${report.acx.checks.length} checks`];
    // The tightest dB margin IS the story: it is the number a distributor's rejection turns on.
    if (tightest) {
      parts.push(`tightest ${tightest.tightestMargin.toFixed(2)} dB on ${tightest.label.toLowerCase()} `
        + `(ch ${tightest.tightestChapter})`);
    }
    if (report.acx.failingChapters.length) parts.push(`${report.acx.failingChapters.length} failing`);
    return parts.join(" · ");
  }, [report]);

  const shortFiles = useMemo(
    () => (report?.artifacts || []).filter((a) => a.missingChapters?.length).length,
    [report]
  );

  const filesSummary = useMemo(() => {
    const n = zips.length + reels.length;
    if (!n) return "none yet";
    const newest = [...zips, ...reels][0];
    return `${zips.length} export${zips.length === 1 ? "" : "s"}, ${reels.length} reel`
      + `${reels.length === 1 ? "" : "s"} · newest ${fmtBytes(newest.bytes)} ${fmtWhen(newest.createdAt)}`;
  }, [zips, reels]);

  const reelSummary = useMemo(() => {
    const joins = Math.max(0, (report?.coverage?.masteredCount || 0) - 1);
    if (!joins) return "needs at least two mastered chapters";
    const newest = reels[0];
    return newest
      ? `built ${fmtWhen(newest.createdAt)} · ${newest.meta?.boundaries ?? joins} joins`
      : `${joins} joins to audition, about ${Math.round(joins * 17.2 / 60)} min`;
  }, [report, reels]);

  if (!bookId) return null;

  const v = report ? VERDICT[report.verdict] || VERDICT.unproven : null;

  return (
    <Segment className="rs-delivery-panel">
      <Header as="h4" style={{ marginBottom: 4 }}>
        <Icon name="shipping fast" />
        <Header.Content>
          Delivery
          <Header.Subheader>
            {unitLabel || selector} — is this finished, and is the file really the book?
          </Header.Subheader>
        </Header.Content>
      </Header>

      <div style={{ marginBottom: 10 }}>
        <Button size="mini" icon labelPosition="left" onClick={load} loading={loading} disabled={loading}>
          <Icon name="refresh" /> Re-read
        </Button>
        {loading && !report && <Loader active inline size="tiny" style={{ marginLeft: 10 }} />}
      </div>

      {error && (
        <Message negative size="small" onDismiss={() => setError(null)}>
          <p>{error}</p>
        </Message>
      )}

      {report && (
        <>
          <Message
            positive={report.verdict === "ready"}
            warning={report.verdict === "unproven"}
            negative={report.verdict === "not-ready"}
            size="small"
          >
            <Message.Header>
              <Icon name={v.icon} /> {v.text}
            </Message.Header>
            <p style={{ marginTop: 6 }}>
              {report.coverage.masteredCount} of {report.coverage.chapters} chapter(s) mastered
              {report.coverage.presets.length === 1 ? ` to ${report.coverage.presets[0]}` : ""}
              {" · "}{fmtHours(report.coverage.runtimeMs)}{" · "}{fmtBytes(report.coverage.bytes)}
            </p>
            {/*
              ⚠️ "Unproven" is not "ready". A check nobody could measure is an open question, and the
              operator is about to stop looking — so the panel says so in words rather than showing a
              green tick with a footnote.
            */}
            {report.verdict === "unproven" && (
              <p style={{ marginTop: 4 }}>
                Nothing is <em>wrong</em>, but at least one check could not be measured — so nothing
                proves this is right either. Re-master the affected chapters to settle it.
              </p>
            )}
            {report.blockers.map((b) => (
              <p key={b.code} style={{ color: "#912d2b", fontWeight: 600, marginTop: 4 }}>
                <Icon name="warning sign" /> {b.message}
                {b.chapters?.length ? ` (ch ${b.chapters.slice(0, 12).join(", ")}${b.chapters.length > 12 ? "…" : ""})` : ""}
              </p>
            ))}
          </Message>

          {/*
            ⭐ EVERY SUMMARY BELOW CARRIES THE NUMBER THAT WOULD HAVE MADE YOU OPEN THE SECTION. A
            collapsed "ACX, as achieved ▸" forces the operator to expand it to learn anything, which
            makes folding a tax rather than a saving — so the tightest margin rides on the outside.
          */}
          <CollapsibleSection
            storageKey="rs.delivery.acx"
            title="ACX, as achieved"
            subtitle="The worst chapter for each check — not the average, because one bad file fails the book."
            summary={acxSummary}
            badge={report.acx.failingChapters.length
              ? <Label color="red" size="mini">{report.acx.failingChapters.length} failing</Label>
              : null}
          >
          <Table compact size="small" basic="very">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Check</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Limit</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Worst</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Margin</Table.HeaderCell>
                <Table.HeaderCell>Where</Table.HeaderCell>
                <Table.HeaderCell>Chapters</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {report.acx.checks.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={6} className="rs-muted">
                    Nothing measured yet — master the unit first.
                  </Table.Cell>
                </Table.Row>
              )}
              {report.acx.checks.map((c) => <CheckRow key={c.key} check={c} />)}
            </Table.Body>
          </Table>
          </CollapsibleSection>

          <CollapsibleSection
            storageKey="rs.delivery.files"
            title="Files"
            subtitle="Every export and reel this book has produced. Links expire; the files do not — ask for a fresh link any time instead of re-exporting."
            summary={filesSummary}
            badge={shortFiles ? <Label color="red" size="mini">{shortFiles} short</Label> : null}
          >

          {zips.length === 0 && reels.length === 0 && (
            <p className="rs-muted">No files yet. Export the unit, or build a seam reel below.</p>
          )}

          {[...zips, ...reels].map((a) => {
            const ver = verified[a.uuid];
            return (
              <Segment key={a.uuid} secondary={!a.isActive} style={{ padding: "8px 10px", marginBottom: 6 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Icon name={a.kind === "export-zip" ? "file archive outline" : "music"} />
                  <strong>{a.label || a.kind}</strong>
                  {!a.isActive && (
                    <Popup
                      size="tiny"
                      trigger={<Label size="mini">superseded</Label>}
                      content="A newer file replaced this one. It is kept because a link to it may already have been shared."
                    />
                  )}
                  <span className="rs-muted">
                    {fmtBytes(a.bytes)}
                    {a.chapters != null ? ` · ${a.chapters} file(s)` : ""}
                    {" · "}{fmtWhen(a.createdAt)}
                  </span>
                </div>

                {!!a.missingChapters?.length && (
                  <p style={{ color: "#912d2b", fontWeight: 600, margin: "6px 0 0" }}>
                    <Icon name="warning sign" />
                    Missing ch {a.missingChapters.join(", ")} — this file is short.
                  </p>
                )}

                {a.meta?.backfilled && (
                  <p className="rs-muted" style={{ margin: "6px 0 0", fontSize: "0.85em" }}>
                    Recorded from storage after the fact — stage and preset were not captured at export
                    time and are deliberately blank rather than guessed.
                  </p>
                )}

                <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                  <Button size="mini" icon labelPosition="left" onClick={() => getLink(a.uuid)}>
                    <Icon name="linkify" /> Get link
                  </Button>
                  {a.kind === "export-zip" && (
                    <Popup
                      size="tiny"
                      content="Reads the zip's own index — names, order and exact byte counts against the chapter rows. Costs kilobytes, not a download."
                      trigger={
                        <Button
                          size="mini"
                          icon
                          labelPosition="left"
                          loading={verifying === a.uuid}
                          disabled={!!verifying}
                          onClick={() => runVerify(a.uuid)}
                        >
                          <Icon name="clipboard check" /> Verify
                        </Button>
                      }
                    />
                  )}
                  {a.kind === "seam-reel" && a.meta?.boundaries != null && (
                    <>
                      <Button
                        size="mini"
                        icon
                        labelPosition="left"
                        onClick={() => setOpenReel(openReel === a.uuid ? null : a.uuid)}
                      >
                        <Icon name={openReel === a.uuid ? "caret up" : "list ol"} />
                        {openReel === a.uuid ? "Hide joins" : `${a.meta.boundaries} joins`}
                      </Button>
                      <span className="rs-muted">
                        {fmtHours(a.meta.durationMs)}
                        {a.meta.gaps?.length ? ` · ⚠️ gaps at ${a.meta.gaps.join(", ")}` : ""}
                      </span>
                    </>
                  )}
                </div>

                {/*
                  ⭐ THE JOIN LIST IS WHAT MAKES THE REEL JUDGEABLE. Audio plus a timecode tells the
                  operator WHERE something is wrong; only the text tells them WHAT is missing, because
                  "the chapter ended mid-word" and "that is how the sentence ends" sound identical
                  unless you know the sentence. Read the row, hear the join, and a dropped word is
                  obvious rather than merely suspicious.

                  ⚠️ This is the text AS SENT TO THE MODEL, not a transcript — nothing listened. It says
                  what SHOULD be there, which is exactly the comparison an ear is being asked to make.
                */}
                {openReel === a.uuid && (
                  <Table compact size="small" basic="very" style={{ marginTop: 8 }}>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>At</Table.HeaderCell>
                        <Table.HeaderCell>Join</Table.HeaderCell>
                        <Table.HeaderCell>…ends with</Table.HeaderCell>
                        <Table.HeaderCell>begins with…</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {(a.meta.index || []).map((b) => (
                        <Table.Row key={b.index} warning={b.gap}>
                          <Table.Cell singleLine>
                            {/* The moment of the join itself, not the start of the pair — that is the
                                instant the operator is being asked to judge. */}
                            <strong>{fmtClock(b.joinAtMs)}</strong>
                          </Table.Cell>
                          <Table.Cell singleLine>
                            {b.fromChapter} → {b.toChapter}
                            {b.gap && (
                              <Popup
                                size="tiny"
                                trigger={<Icon name="warning sign" color="orange" style={{ marginLeft: 4 }} />}
                                content="These chapter numbers are not consecutive. That may be a missing chapter, or a number the book simply never uses — Mask skips 11 in print, with continuous text. Either way this is the join a listener will hear, so it is worth auditioning."
                              />
                            )}
                            {(b.fromTitle || b.toTitle) && (
                              <div className="rs-muted" style={{ fontSize: "0.85em" }}>
                                {b.toTitle || ""}
                              </div>
                            )}
                          </Table.Cell>
                          <Table.Cell style={{ fontStyle: "italic" }}>
                            {b.endsWith || <span className="rs-muted">no text available</span>}
                          </Table.Cell>
                          <Table.Cell style={{ fontStyle: "italic" }}>
                            {b.beginsWith || <span className="rs-muted">no text available</span>}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                      {!(a.meta.index || []).length && (
                        <Table.Row>
                          <Table.Cell colSpan={4} className="rs-muted">
                            This reel was built before join text was recorded — rebuild it to get the list.
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table>
                )}

                {link?.uuid === a.uuid && (
                  <Message size="tiny" info style={{ marginTop: 8 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <Button
                        size="mini"
                        icon
                        labelPosition="left"
                        color={copied ? "green" : undefined}
                        onClick={() => {
                          navigator.clipboard?.writeText(link.signedUrl);
                          setCopied(true);
                        }}
                      >
                        <Icon name={copied ? "check" : "copy"} />
                        {copied ? "Copied" : "Copy link"}
                      </Button>
                      <Button size="mini" icon labelPosition="left" as="a" href={link.signedUrl} target="_blank" rel="noreferrer">
                        <Icon name="download" /> Download
                      </Button>
                    </div>
                    {/*
                      State the security property plainly — a signed URL is privacy by obscurity with an
                      expiry, NOT access control, and whoever shares it needs to know that.
                    */}
                    <p style={{ marginTop: 8, fontSize: "0.85em" }}>
                      <Icon name="lock open" /> Anyone with this link can download it until{" "}
                      <strong>{fmtWhen(link.expiresAt)}</strong>. It is not password-protected.
                      {link.objectBytes != null && link.objectBytes !== a.bytes && (
                        <>
                          {" "}⚠️ Storage reports {fmtBytes(link.objectBytes)} where the record says{" "}
                          {fmtBytes(a.bytes)} — the file is the truth; re-verify it.
                        </>
                      )}
                    </p>
                  </Message>
                )}

                {ver && (
                  <Message
                    size="tiny"
                    positive={ver.verdict === "verified-by-size"}
                    negative={!!ver.error || ver.verdict === "problems"}
                    style={{ marginTop: 8 }}
                  >
                    {ver.error ? <p>{ver.error}</p> : (
                      <>
                        <Message.Header>
                          <Icon name={ver.verdict === "problems" ? "warning sign" : "check"} />
                          {ver.verdict === "problems"
                            ? `${ver.problems.length} problem(s) found`
                            : `${ver.matched.length} of ${ver.entryCount} entries match the mastered chapters exactly`}
                        </Message.Header>
                        {ver.problems?.map((p) => (
                          <p key={p.code} style={{ margin: "4px 0 0", fontWeight: 600 }}>
                            {p.message}
                            {p.chapters?.length ? ` (ch ${p.chapters.slice(0, 12).join(", ")})` : ""}
                          </p>
                        ))}
                        <p className="rs-muted" style={{ marginTop: 6, fontSize: "0.85em" }}>
                          Read {fmtBytes(ver.bytesRead)} of {fmtBytes(ver.zipBytes)}. {ver.proves}
                        </p>
                      </>
                    )}
                  </Message>
                )}
              </Segment>
            );
          })}

          </CollapsibleSection>

          <CollapsibleSection
            storageKey="rs.delivery.reel"
            title="Seam reel"
            subtitle="Every chapter join in one file — the last seconds of each chapter butted against the first seconds of the next, exactly as a player will play them."
            summary={reelSummary}
            defaultOpen={false}
          >
          <p className="rs-muted" style={{ marginTop: 0 }}>
            {/*
              The argument for the tool, in one sentence, because an operator deciding whether to spend
              three minutes on it needs the reason and not the mechanism.
            */}
            Nobody auditions {fmtHours(report.coverage.runtimeMs)} of book, so in practice nobody
            auditions it at all — and everything that goes wrong in assembly goes wrong at a join.
            {report.coverage.masteredCount > 1
              ? ` ${report.coverage.masteredCount - 1} joins, about ${Math.round((report.coverage.masteredCount - 1) * 17.2 / 60)} minutes.`
              : ""}
          </p>
          <Button
            size="small"
            icon
            labelPosition="left"
            primary
            loading={reeling}
            disabled={reeling || report.coverage.masteredCount < 2}
            onClick={runReel}
          >
            <Icon name="music" /> Build seam reel
          </Button>
          {reeling && (
            <span className="rs-muted" style={{ marginLeft: 10 }}>
              Fetching the bytes around each join and re-encoding — a few minutes.
            </span>
          )}
          {reelMsg && (
            <Message positive size="tiny" style={{ marginTop: 8 }} onDismiss={() => setReelMsg(null)}>
              {reelMsg}
            </Message>
          )}
          </CollapsibleSection>
        </>
      )}
    </Segment>
  );
};

export default DeliveryPanel;
